import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

import { fetchGifPage, type GifPage, type GifSearchSource, type KlipyClientOptions, type KlipyGif } from "./klipy-gifs";

interface UseGifSearchOptions extends Omit<KlipyClientOptions, "fetchFn"> {
  /** Already debounced by the caller — every distinct value is a request. */
  query: string;
  enabled?: boolean;
}

const STALE_TIME = 5 * 60 * 1000;

/**
 * Trending when the query is empty, search otherwise; paged with KLIPY's `has_next`. Results are
 * kept in the order KLIPY returns them (their terms), only de-duplicated across pages.
 */
export const useGifSearch = ({ appKey, customerId, locale, query, enabled = true }: UseGifSearchOptions) => {
  const trimmedQuery = query.trim();
  const source: GifSearchSource = trimmedQuery ? "search" : "trending";

  const infiniteQuery = useInfiniteQuery<GifPage, Error>(
    ["klipy-gifs", source, trimmedQuery, locale ?? null, customerId ?? null],
    ({ pageParam = 1 }) => fetchGifPage({ appKey, customerId, locale }, { query: trimmedQuery, page: pageParam }),
    {
      enabled: enabled && Boolean(appKey),
      getNextPageParam: (lastPage) => (lastPage.hasNext ? lastPage.page + 1 : undefined),
      staleTime: STALE_TIME,
      keepPreviousData: true,
      retry: 1,
    },
  );

  const gifs = useMemo<KlipyGif[]>(() => {
    const seen = new Set<string>();
    const result: KlipyGif[] = [];
    for (const page of infiniteQuery.data?.pages ?? []) {
      for (const gif of page.gifs) {
        if (!seen.has(gif.slug)) {
          seen.add(gif.slug);
          result.push(gif);
        }
      }
    }
    return result;
  }, [infiniteQuery.data]);

  return {
    gifs,
    source,
    query: trimmedQuery,
    isLoading: infiniteQuery.isLoading,
    isError: infiniteQuery.isError,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    /** True while `gifs` still belong to the previous query (kept on screen to avoid a flash). */
    isPreviousData: infiniteQuery.isPreviousData,
    hasNextPage: Boolean(infiniteQuery.hasNextPage),
    fetchNextPage: infiniteQuery.fetchNextPage,
    refetch: infiniteQuery.refetch,
  };
};
